import React, { useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import Input from '../Inputs/Input';
import { calculateRollCount } from '../../Services/WallpaperCalculatorService';
import SideMenu from '../SideMenu/SideMenu';
import BuyButton from '../BuyButton/BuyButton';
import { add as addToCart } from '../../Services/Cart.service';
import { receive } from '../../Actions/Cart.action';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import Icon from '../Icon';
import WallpaperCalculatorResult from './WallpaperCalculatorResult';

interface WallpaperCalculatorModalProps {
  articleNumber: string;
  rollWidth: number;
  rollLength: number;
  patternSizeMeters: number;
  fitment: number;
  open: boolean;
  hasPrice: boolean;
  onClosed: () => void;
  onAddedToCart: () => void;
}

const WallpaperCalculatorModal = ({
  articleNumber,
  rollWidth,
  rollLength,
  patternSizeMeters,
  fitment,
  open,
  hasPrice,
  onClosed,
  onAddedToCart,
}: WallpaperCalculatorModalProps) => {
  const dispatch = useTypedDispatch();
  const [wallWidth, setWallWidth] = useState<number | undefined>();
  const [wallHeight, setWallHeight] = useState<number | undefined>();

  const calculatorResult = useMemo(
    () =>
      wallWidth && wallHeight && rollWidth && rollLength
        ? calculateRollCount({
            wallWidth,
            wallHeight,
            rollLength,
            rollWidth,
            patternSize: patternSizeMeters ?? 0.01,
            fitment: fitment ?? 0,
          })
        : undefined,
    [
      wallWidth,
      wallHeight,
      rollWidth,
      rollLength,
      patternSizeMeters,
      fitment,
    ]
  );

  const onSubmitClick = async (e) => {
    e.preventDefault();
    if (!calculatorResult?.rolls) return;

    // Add to cart, then close the modal after 1000 ms
    const cart = await addToCart({
      articleNumber,
      quantity: Math.ceil(calculatorResult?.rolls),
    });
    dispatch(receive(cart));
    setTimeout(() => onAddedToCart(), 1000);
  };

  return (
    <SideMenu
      id="side-menu-modal"
      open={open}
      onClosed={onClosed}
      showCloseButton={false}
      contentClassName="p-7 pt-6 lg:p-12 lg:pt-6"
      className="rounded"
    >
      <h2 className="mb-2">{translate('wallpaper-calculator')}</h2>
      <div className="mb-6 text-xl font-light">
        {translate('wallpaper-calculator.title')}
      </div>
      <form onSubmit={onSubmitClick}>
        <div className="grid grid-cols-1 gap-2">
          <Input
            label={`${translate('wallpaper-calculator.wall-width')} (m)`}
            type="number"
            step="0.01"
            value={wallWidth ?? ''}
            onChange={(e) =>
              setWallWidth(
                e.target.value != ''
                  ? parseFloat(e.target.value)
                  : undefined
              )
            }
            showAnimatedLabel={true}
            inputSize="large"
          />
          <Input
            label={`${translate('wallpaper-calculator.wall-height')} (m)`}
            type="number"
            step="0.01"
            value={wallHeight ?? ''}
            onChange={(e) =>
              setWallHeight(
                e.target.value != ''
                  ? parseFloat(e.target.value)
                  : undefined
              )
            }
            showAnimatedLabel={true}
            inputSize="large"
          />
        </div>
        <div className="mt-4 flex flex-col text-lg leading-normal">
          {calculatorResult && calculatorResult.rolls > 0 ? (
            <WallpaperCalculatorResult
              {...calculatorResult}
              showWaste={fitment > 0}
            />
          ) : (
            <span>{translate('wallpaper-calculator.fill-in-form')}</span>
          )}
        </div>
        {hasPrice && (
          <BuyButton
            className="mt-4"
            type="submit"
            onClick={onSubmitClick}
            disabled={
              !calculatorResult?.rolls ||
              calculatorResult.rolls <= 0 ||
              Number.isNaN(calculatorResult.rolls)
            }
            label={
              <>
                <Icon name="shopping-cart" className="mr-3 h-6 w-6" />{' '}
                {translate(
                  'wallpaper-calculator.add-rolls-to-cart'
                ).replace(
                  '{0}',
                  Math.ceil(calculatorResult?.rolls ?? 0).toString()
                )}
              </>
            }
          />
        )}
      </form>
      <button
        onClick={onClosed}
        className="btn btn--white btn--small mt-6 w-full text-center text-md font-normal uppercase leading-4 underline"
      >
        {translate('general.cancel')}
      </button>
    </SideMenu>
  );
};

export default WallpaperCalculatorModal;
